import avatarSrc from "../../helpers/avatarSrc";
import React, { useState, useEffect } from "react";

import { Avatar, CardHeader, makeStyles, Box } from "@material-ui/core";
import ChannelBadge from "../ChannelBadge";

import { i18n } from "../../translate/i18n";
import { getInitials } from "../../helpers/getInitials";
import { generateColor } from "../../helpers/colorGenerator";

const useStyles = makeStyles((theme) => {
	const isDark = theme.palette.type === "dark";
	return {
		channelLabel: {
			fontSize: "10px",
			fontWeight: 700,
			padding: "2px 8px",
			borderRadius: 6,
			color: "#fff",
			marginRight: "8px",
			display: "inline-block",
			verticalAlign: "middle",
			letterSpacing: 0.3,
			textTransform: "uppercase",
			[theme.breakpoints.down("xs")]: {
				fontSize: "8px",
				padding: "1px 5px",
				marginRight: "4px",
			},
		},
		titleContainer: {
			display: "flex",
			alignItems: "center",
			flexWrap: "wrap",
			gap: 6,
		},
		titleName: {
			fontWeight: 700,
			fontSize: "0.95rem",
			color: isDark ? "#e6f7ec" : "#102b1f",
			lineHeight: 1.2,
		},
		titleTicketId: {
			fontSize: "0.72rem",
			fontWeight: 600,
			color: isDark ? "rgba(255,255,255,0.45)" : "#64748b",
			fontFamily: '"SF Mono", "Menlo", "Consolas", monospace',
			padding: "2px 6px",
			borderRadius: 6,
			background: isDark ? "rgba(255,255,255,0.04)" : "#f1f5f3",
			border: isDark
				? "1px solid rgba(255,255,255,0.06)"
				: "1px solid rgba(0,0,0,0.04)",
		},
		// Card header shell
		cardHeader: {
			cursor: "pointer",
			padding: "4px 4px",
			marginLeft: "auto",
			borderRadius: 12,
			transition: "background 0.15s ease",
			"&:hover": {
				background: isDark ? "rgba(255,255,255,0.03)" : "#f8faf9",
			},
			"& .MuiCardHeader-content": {
				overflow: "hidden",
			},
			"& .MuiCardHeader-subheader": {
				fontSize: "0.78rem",
				color: isDark ? "rgba(230,247,236,0.55)" : "#5f6f6a",
				fontWeight: 500,
				marginTop: 2,
			},
			[theme.breakpoints.down("xs")]: {
				padding: "4px 12px",
				width: "100%",
				marginLeft: "0 !important",
				marginRight: "0 !important",
				borderRadius: 0,
				backgroundColor: isDark ? "#131614" : "#f8fafc",
				borderBottom: `1px solid ${theme.palette.divider}`,
				flexDirection: theme.direction === "rtl" ? "row-reverse" : "row",
				"& .MuiCardHeader-avatar": {
					marginRight: theme.direction === "rtl" ? 0 : theme.spacing(1),
					marginLeft: theme.direction === "rtl" ? theme.spacing(1) : 0,
				},
				"& .MuiCardHeader-content": {
					textAlign: theme.direction === "rtl" ? "right" : "left",
					flex: 1,
					minWidth: 0,
				},
				"& .MuiCardHeader-title": {
					fontSize: "0.85rem",
				},
				"& .MuiCardHeader-subheader": {
					fontSize: "0.7rem",
				},
			},
		},
		// Avatar with brand ring + online dot
		avatarWrap: {
			position: "relative",
			display: "inline-flex",
		},
		avatar: {
			fontWeight: 700,
			width: 42,
			height: 42,
			fontSize: "1rem",
			border: isDark
				? "2px solid rgba(46,227,122,0.18)"
				: "2px solid rgba(37,211,102,0.20)",
			boxShadow: isDark
				? "0 4px 10px rgba(0,0,0,0.35)"
				: "0 4px 10px rgba(15,23,42,0.06)",
			[theme.breakpoints.down("xs")]: {
				width: 32,
				height: 32,
				fontSize: "0.85rem",
				border: "none",
				boxShadow: "none",
			},
		},
	};
});

const TicketInfo = ({ contact, ticket, onClick }) => {
	const classes = useStyles();
	const { user, whatsapp } = ticket;
	const [userName, setUserName] = useState("");
	const [contactName, setContactName] = useState("");

	useEffect(() => {
		if (contact) {
			setContactName(contact.name);
		}

		if (user && contact) {
			if (document.body.offsetWidth < 600) {
				setUserName(`${user.name}`);
			} else {
				setUserName(`${i18n.t("messagesList.header.assignedTo")} ${user.name}`);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contact, user]);

	const renderTitle = () => (
		<Box className={classes.titleContainer}>
			{whatsapp?.channelLabel && (
				<span
					className={classes.channelLabel}
					style={{
						backgroundColor: whatsapp?.channelLabelColor || "#FF9800",
					}}
				>
					{whatsapp.channelLabel}
				</span>
			)}
			<span className={classes.titleName}>{contactName}</span>
			<span className={classes.titleTicketId}>#{ticket.id}</span>
			{(ticket.channel === "facebook" || ticket.channel === "instagram" || ticket.channel === "telegram") && (
				<ChannelBadge channel={ticket.channel} />
			)}
		</Box>
	);

	return (
		<CardHeader
			onClick={onClick}
			className={classes.cardHeader}
			titleTypographyProps={{ noWrap: true }}
			subheaderTypographyProps={{ noWrap: true }}
			avatar={
				<span className={classes.avatarWrap}>
					<Avatar
						className={classes.avatar}
						style={{
							backgroundColor: generateColor(contact?.number),
							color: "white",
						}}
						src={avatarSrc(contact.profilePicUrl)}
						alt="contact_image"
					>
						{getInitials(contact?.name)}
					</Avatar>
				</span>
			}
			title={renderTitle()}
			subheader={ticket.user && `${userName}`}
		/>
	);
};

export default TicketInfo;
